/**
Author - Vikrant Singh
Git - git@github.com:vikrantSingh97
**/

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ActionButton, ActionButtonSecondary, Spinner} from 'screens/commons';
import {ProgressBar} from 'components/ProgressBar';

import IMAGES from 'constants/Images';

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isArnDetected: PropTypes.bool.isRequired,
  partnerDetails: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  redirectingUser: PropTypes.bool.isRequired,
  fetchingArn: PropTypes.bool.isRequired,
  onSkipArn: PropTypes.bool.isRequired,
  onUploadArn: PropTypes.bool.isRequired,
};

const CheckArn = ({
  onSubmit,
  isArnDetected,
  partnerDetails,
  isLoading,
  fetchingArn,
  redirectingUser,
  onSkipArn,
  onUploadArn,
}) => {
  const showLoader = isLoading || redirectingUser || fetchingArn;

  return showLoader ? (
    <LoaderContainer>
      <ProgressBar
        subheading={fetchingArn ? 'This might take a few moments' : ''}
        heading={fetchingArn ? 'Searching for ARN' : 'Updating your KYC information'}
        waitTimeInSec={180}
        stopOnEndTime={true}
        stopOnEndMsg={'Search for ARN timed out. Please try again...'}
        showShadow={true}
        loadingTexts={
          fetchingArn
            ? ['Adding ARN details allows you to pay a reduced fee for Empanelment']
            : undefined
        }
      />
    </LoaderContainer>
  ) : (
    <>
      <CardContainer>
        <>
          <HeaderContent>
            <StatusBadge isSuccess={isArnDetected}>
              {isArnDetected ? (
                <>
                  <StatusImage src={IMAGES.arnDetected} alt="ARN Detected" height={68} width={68} />
                  <StatusText className="body-regular font-semibold m-0">ARN Detected</StatusText>
                </>
              ) : (
                <>
                  <StatusImage src={IMAGES.arnFailed} alt="ARN Not Found" height={68} width={68} />
                  <StatusText className="body-regular font-semibold m-0">ARN Not Found</StatusText>
                </>
              )}
            </StatusBadge>
            <CardDescription className="body-small">
              {isArnDetected ? (
                <>
                  {partnerDetails.arnNo} ARN was detected for PAN {partnerDetails.panNumber}. To
                  accept ARN, Please sign the agreement by checking the agreement.
                </>
              ) : (
                <>
                  We couldn't find any ARN associated with your PAN {partnerDetails.panNumber}. You
                  can upload your ARN card or skip this step.
                </>
              )}
            </CardDescription>
          </HeaderContent>

          {!isArnDetected && (
            <CardContent>
              <UploadInfoBox>
                <UploadInfoContent>
                  <ImageContainer className="flex-shrink-0">
                    <ArnCardImage
                      src={IMAGES.arnCard}
                      alt="ARN Card Example"
                      height={130}
                      className="rounded-md"
                    />
                  </ImageContainer>
                  <InfoContentContainer className="flex-1">
                    <InfoHeading className="body-regular font-medium mb-2">
                      Upload your ARN Card
                    </InfoHeading>
                    <InfoDescription className="body-small mb-4">
                      You can upload an image of your ARN card similar to the illustration shown
                      here
                    </InfoDescription>
                    <InfoList className="space-y-2 text-sm">
                      <InfoListItem className="flex items-start gap-2">
                        <ArrowIcon
                          src={IMAGES.arrowRight}
                          alt="arrow"
                          height={12}
                          width={12}
                          className="mt-1"
                        />
                        <InfoListText>Make sure the ARN number is clearly visible</InfoListText>
                      </InfoListItem>
                      <InfoListItem className="flex items-start gap-2">
                        <ArrowIcon
                          src={IMAGES.arrowRight}
                          alt="arrow"
                          height={12}
                          width={12}
                          className="mt-1"
                        />
                        <InfoListText>Ensure the image is well-lit and not blurry</InfoListText>
                      </InfoListItem>
                    </InfoList>
                  </InfoContentContainer>
                </UploadInfoContent>
              </UploadInfoBox>

              <InfoBox>
                <InfoBoxHeading className="body-regular mb-1 d-flex align-items-center">
                  <QuestionIcon
                    src={IMAGES.questionMark}
                    alt="Question Mark"
                    className="m-2"
                    height={18}
                    width={18}
                  />
                  Why ARN Details ?
                </InfoBoxHeading>
                <InfoBoxText className="body-small">
                  Adding ARN details allows you to pay a reduced fee for Empanelment
                </InfoBoxText>
              </InfoBox>
            </CardContent>
          )}

          <CardFooter className={isArnDetected ? 'mt-4' : ''}>
            {isArnDetected ? (
              <ActionButton buttonText="Continue" callback={onSubmit} />
            ) : (
              <>
                <ActionButtonSecondary
                  buttonText="Skip ARN steps"
                  callback={onSkipArn}
                  additionalStyles={{
                    marginRight: '1rem',
                    order: 1,
                  }}
                />
                <ActionButton
                  buttonText="Upload ARN Card Image"
                  callback={onUploadArn}
                  additionalStyles={{
                    order: 2,
                  }}
                />
              </>
            )}
          </CardFooter>
        </>
      </CardContainer>
    </>
  );
};

const StatusText = styled.p`
  margin: 0;
`;

const StatusImage = styled.img`
  display: block;
`;

const ArrowIcon = styled.img`
  display: block;
`;

const QuestionIcon = styled.img`
  display: block;
`;

const ArnCardImage = styled.img`
  border-radius: 0.375rem;
  display: block;
`;

const ImageContainer = styled.div`
  flex-shrink: 0;
`;

const InfoContentContainer = styled.div`
  flex: 1;
`;

const InfoHeading = styled.h3`
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0 0 0.5rem;
`;

const InfoDescription = styled.p`
  color: ${(props) => props.theme.colors.text.secondary};
  margin: 0 0 1rem 0;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoListText = styled.span`
  color: ${(props) => props.theme.colors.text.secondary};
`;

const InfoBoxHeading = styled.h4`
  display: flex;
  align-items: center;
  margin: 0;
`;

const InfoBoxText = styled.p`
  color: ${(props) => props.theme.colors.text.secondary};
  margin: 0;
  margin-left: 1.5rem;
`;

const CardContainer = styled.div`
  width: 100%;
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 9999px;
  font-weight: 500;
  color: ${(props) =>
    props.isSuccess ? props.theme.colors.feedback.success : props.theme.colors.feedback.warning};
`;

const CardDescription = styled.p`
  margin: 0;
  color: ${(props) => props.theme.colors.text.secondary};
`;

const HeaderContent = styled.div`
  width: 100%;
  border-bottom: 1px solid ${(props) => props.theme.colors?.border?.default || '#e2e8f0'};
  padding: 1rem 1.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints?.md || '768px'}) {
    border-bottom: none;
    padding: 1rem;
    padding-top: 0;
  }
`;

const CardContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 700px;
  margin: 0 auto;
`;

const UploadInfoBox = styled.div`
  background-color: rgba(59, 130, 246, 0.05);
  border-radius: ${(props) => props.theme.borderRadius?.md || '0.375rem'};

  padding: 1.5rem;
`;

const UploadInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const InfoBox = styled.div`
  background-color: ${(props) => `${props.theme.colors.brand.secondary}`};
  border-radius: ${(props) => props.theme.borderRadius?.md || '0.375rem'};
  border: 1px solid ${(props) => props.theme.colors.brand.primary};

  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const CardFooter = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 700px;
  margin: 0 auto;
  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: ${(props) => (props.isArnDetected ? 'center' : 'flex-end')};
  }
`;

const LoaderContainer = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 25px;
    height: unset;
  }
`;

CheckArn.propTypes = propTypes;

export default CheckArn;
